export enum RegisterServiceType {
  SnackSubscription = "snack-subscription",
  UnmannedConvenience = "unmanned-convenience",
}

export type UserRole = "admin" | "user";

export const SECRET_CODE_CONFIG: Record<
  string,
  { role: UserRole; affiliation: string }
> = {
  "24070901": { role: "admin", affiliation: "총무노트" },
  "24070902": { role: "user", affiliation: "우리자산운용" },
  "24070903": { role: "user", affiliation: "KB자산운용" },
  "24071120": { role: "user", affiliation: "코인원" },
};

export const VALID_SECRET_CODES = Object.keys(SECRET_CODE_CONFIG);

export enum SnackSubscriptionEmailDomain {
  Naver = "@naver.com",
  Gmail = "@gmail.com",
  Sionic = "@sionic.ai",
  Szimpatikus = "@szimpatikus.kr",
  Appier = "@appier.com",
  Triptopaz = "@triptopaz.com",
  Octopo = "@octopo.art",
  Quanda = "@mathpresso.com",
  Locknlock = "@locknlock.com",
}

export const SNACK_SUBSCRIPTION_DOMAIN_CONFIG: Record<
  SnackSubscriptionEmailDomain,
  { label: string; affiliation: string }
> = {
  [SnackSubscriptionEmailDomain.Naver]: {
    label: "@naver.com",
    affiliation: "네이버",
  },
  [SnackSubscriptionEmailDomain.Gmail]: {
    label: "@gmail.com",
    affiliation: "구글",
  },
  [SnackSubscriptionEmailDomain.Sionic]: {
    label: "@sionic.ai",
    affiliation: "사이오닉AI",
  },
  [SnackSubscriptionEmailDomain.Szimpatikus]: {
    label: "@szimpatikus.kr",
    affiliation: "심퍼티쿠시",
  },
  [SnackSubscriptionEmailDomain.Appier]: {
    label: "@appier.com",
    affiliation: "애피어코리아",
  },
  [SnackSubscriptionEmailDomain.Triptopaz]: {
    label: "@triptopaz.com",
    affiliation: "트립토파즈",
  },
  [SnackSubscriptionEmailDomain.Octopo]: {
    label: "@octopo.art",
    affiliation: "옥토포",
  },
  [SnackSubscriptionEmailDomain.Quanda]: {
    label: "@mathpresso.com",
    affiliation: "콴다",
  },
  [SnackSubscriptionEmailDomain.Locknlock]: {
    label: "@locknlock.com",
    affiliation: "락앤락",
  },
};

export const SNACK_SUBSCRIPTION_DOMAIN_OPTIONS = Object.entries(
  SNACK_SUBSCRIPTION_DOMAIN_CONFIG,
).map(([value, meta]) => ({
  value,
  label: meta.label,
  affiliation: meta.affiliation,
}));

type SnackSubscriptionSecretCodeMeta = {
  role: UserRole;
  domain: SnackSubscriptionEmailDomain;
  label: string;
  affiliation: string;
};

// Add new customer codes by continuing the sequence after the latest code.
const SNACK_SUBSCRIPTION_SECRET_CODE_ENTRIES: Array<
  [string, SnackSubscriptionEmailDomain]
> = [
  ["250811", SnackSubscriptionEmailDomain.Naver],
  ["250812", SnackSubscriptionEmailDomain.Gmail],
  ["250813", SnackSubscriptionEmailDomain.Sionic],
  ["250814", SnackSubscriptionEmailDomain.Szimpatikus],
  ["250815", SnackSubscriptionEmailDomain.Appier],
  ["250816", SnackSubscriptionEmailDomain.Triptopaz],
  ["250817", SnackSubscriptionEmailDomain.Octopo],
  ["250818", SnackSubscriptionEmailDomain.Quanda],
  ["250819", SnackSubscriptionEmailDomain.Locknlock],
];

export const SNACK_SUBSCRIPTION_SECRET_CODE_CONFIG: Record<
  string,
  SnackSubscriptionSecretCodeMeta
> = Object.fromEntries(
  SNACK_SUBSCRIPTION_SECRET_CODE_ENTRIES.map(([secretCode, domain]) => [
    secretCode,
    {
      role: "user",
      domain,
      label: SNACK_SUBSCRIPTION_DOMAIN_CONFIG[domain].label,
      affiliation: SNACK_SUBSCRIPTION_DOMAIN_CONFIG[domain].affiliation,
    },
  ]),
) as Record<string, SnackSubscriptionSecretCodeMeta>;

export const SNACK_SUBSCRIPTION_SECRET_CODES = Object.keys(
  SNACK_SUBSCRIPTION_SECRET_CODE_CONFIG,
);

export const SNACK_SUBSCRIPTION_SECRET_CODE_LENGTH =
  SNACK_SUBSCRIPTION_SECRET_CODES[0]?.length ?? 6;
